import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import "./DMapStyle.css";
import { bbox } from "@turf/turf";
import { getProjectCoordinates } from '../../../../utils/mapDataMapping';
import { 
  Tipligheri, 
  Maipith, 
  Dwariknagar, 
  SHIVGURI_CR_Demarcation2025, 
  MER_CR_Demarcation2025, 
  PS0000149,
  PS000060,
  PS0000107,
  PS0000139,
  PS0000132,
  Paglam_CR_Demarcation2025,
  Namsing_CR_Demarcation2025
} from "../../../../mock/dummyGeoJson";
import { FeatureCollection } from "geojson";
import LayerControl from "./LayerControl";
import { getMapStyleUrl, getMapAttribution, getMapStyleOptions } from '../../../../utils/mapStyles';
import { getDeviceLayersForProject } from '../../../../utils/mapLayerUtils';

type DMapAnalysisProps = {
  siteDropDownDetails?: any;
};

const getBoundaryForSite = (siteName?: string): FeatureCollection | null => {
  if (siteName === "Dwariknagar" || siteName === "Drawiknagar") return Dwariknagar as FeatureCollection;
  if (siteName === "Tipligheri") return Tipligheri as FeatureCollection;
  if (siteName === "Maipith") return Maipith as FeatureCollection;
  if (siteName === "SHIVGURI_CR_Demarcation2025" || siteName === "Shivguri") return SHIVGURI_CR_Demarcation2025 as FeatureCollection;
  if (siteName === "Mer") return MER_CR_Demarcation2025 as FeatureCollection;
  if (siteName === "Namsing Proxy" || siteName === "Namsing Original") return Namsing_CR_Demarcation2025 as FeatureCollection;
  if (siteName === "Paglam") return Paglam_CR_Demarcation2025 as FeatureCollection;
  if (siteName === "PS0000149") return PS0000149 as FeatureCollection;
  if (siteName === "PS000060") return PS000060 as FeatureCollection;
  if (siteName === "PS0000107") return PS0000107 as FeatureCollection;
  if (siteName === "PS0000139") return PS0000139 as FeatureCollection;
  if (siteName === "PS0000132") return PS0000132 as FeatureCollection;
  return null;
};

const DMapAnalysis: React.FC<DMapAnalysisProps> = ({ siteDropDownDetails }) => {
  const siteName = siteDropDownDetails?.site_name || "Dwariknagar";
  const DEVICE_LAYERS = useMemo(() => getDeviceLayersForProject(siteName), [siteName]);

  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const [mapStyle, setMapStyle] = useState<string>('satellite');
  const [activeLayers, setActiveLayers] = useState<string[]>([]);
  const [layers, setLayers] = useState<{ id: string; name: string; opacity: number }[]>([]);
  const mapInitialized = useRef<boolean>(false);

  const initializeMap = useCallback((data: FeatureCollection) => {
    if (!mapContainer.current) return;
    if (mapRef.current) { 
        mapRef.current.remove(); 
        mapRef.current = null; 
    }

    const center = getProjectCoordinates(siteName);
    const centerLatLng: [number, number] = Array.isArray(center) && center.length >= 2 ? [Number(center[1]), Number(center[0])] : [21.7125, 88.2625];

    mapRef.current = L.map(mapContainer.current as HTMLDivElement, { center: centerLatLng, zoom: 13, zoomControl: true });
    tileLayerRef.current = L.tileLayer(getMapStyleUrl(mapStyle), getMapStyleOptions(mapStyle)).addTo(mapRef.current);

    const layerId = "boundary-fill";
    const boundaryLayer = L.geoJSON(data as any, {
      style: () => ({ fillColor: '#90EE90', fillOpacity: 0.3, color: '#006400', weight: 2 }),
      filter: (feature: any) => feature && feature.geometry && feature.geometry.type === 'Polygon',
      onEachFeature: (feature: any, layer: any) => {
        if (feature.properties) layer.bindPopup(Object.entries(feature.properties).map(([k,v]) => `<strong>${k}:</strong> ${v}`).join('<br/>'));
      }
    }).addTo(mapRef.current);

    const deviceLayerGroups: Record<string, L.LayerGroup> = {};
    DEVICE_LAYERS.forEach(({ type }) => {
      const deviceLayerId = `device-${type.replace(/\s+/g, '-').toLowerCase()}`;
      deviceLayerGroups[deviceLayerId] = L.layerGroup();
    });

    try {
      const features = (data as any).features || [];
      features.forEach((f: any) => {
        if (!f || !f.geometry) return;
        if (f.geometry.type === 'Point') {
          const coords = f.geometry.coordinates;
          const latlng: [number, number] = [coords[1], coords[0]];
          const type = f.properties?.Type || f.properties?.type || '';
          const layerKey = `device-${String(type).replace(/\s+/g, '-').toLowerCase()}`;
          const color = DEVICE_LAYERS.find((dl: any) => dl.type === type)?.color || '#ff5722';
          const marker = L.circleMarker(latlng, { radius: 10, fillColor: color, color: '#fff', weight: 2.5, fillOpacity: 1 });
          if (f.properties) marker.bindPopup(Object.entries(f.properties).map(([k, v]) => `<strong>${k}:</strong> ${v}`).join('<br/>'));
          deviceLayerGroups[layerKey]?.addLayer(marker);
          marker.on('mouseover', () => { if (mapRef.current) (mapRef.current.getContainer() as HTMLElement).style.cursor = 'pointer'; });
          marker.on('mouseout', () => { if (mapRef.current) (mapRef.current.getContainer() as HTMLElement).style.cursor = ''; });
        }
      });
    } catch (e) { console.warn('[DMapAnalysis] Failed to populate device layers', e); }

    (mapRef.current as any)._deviceLayerGroups = deviceLayerGroups;
    (mapRef.current as any)._boundaryLayer = boundaryLayer;

    Object.values(deviceLayerGroups).forEach(g => g.addTo(mapRef.current as L.Map));

    setLayers([
      { id: layerId, name: 'Site Boundary', opacity: 0.3 },
      ...DEVICE_LAYERS.map((d: any) => ({ id: `device-${d.type.replace(/\s+/g, '-').toLowerCase()}`, name: d.label, opacity: 1 })),
    ]);
    setActiveLayers([layerId, ...DEVICE_LAYERS.map((d: any) => `device-${d.type.replace(/\s+/g, '-').toLowerCase()}`)]);

    try {
      const siteBbox = bbox(data as unknown as FeatureCollection);
      if (Array.isArray(siteBbox) && siteBbox.length === 4 && mapRef.current) {
        const sw: [number, number] = [siteBbox[1], siteBbox[0]];
        const ne: [number, number] = [siteBbox[3], siteBbox[2]];
        (mapRef.current as L.Map).fitBounds([sw, ne], { padding: [40,40] });
      }
    } catch (err) {
      console.error("[DMapAnalysis] fitBounds error:", err);
    }
    
    mapInitialized.current = true;
  }, [siteName, mapStyle, DEVICE_LAYERS]);

  useEffect(() => {
    const boundary = getBoundaryForSite(siteName);
    if (boundary) {
      initializeMap(boundary);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      mapInitialized.current = false;
    };
  }, [siteName, initializeMap]);

  // Handle map style changes without recreating the map
  useEffect(() => {
    if (!mapRef.current || !mapInitialized.current || !tileLayerRef.current) return;
    mapRef.current.removeLayer(tileLayerRef.current);
    tileLayerRef.current = L.tileLayer(getMapStyleUrl(mapStyle), getMapStyleOptions(mapStyle)).addTo(mapRef.current);
  }, [mapStyle]);

  const toggleLayerVisibility = (layer: string) => {
    const isActive = activeLayers.includes(layer);
    setActiveLayers(isActive ? activeLayers.filter(id => id !== layer) : [...activeLayers, layer]);
    const map = mapRef.current as any;
    if (!map) return;
    const deviceGroups: Record<string, L.LayerGroup> = map._deviceLayerGroups || {};
    if (deviceGroups[layer]) {
      if (isActive) map.removeLayer(deviceGroups[layer]); else deviceGroups[layer].addTo(map);
      return;
    }
    const boundaryLayer: L.Layer = map._boundaryLayer;
    if (layer === 'boundary-fill' && boundaryLayer) {
      if (isActive) map.removeLayer(boundaryLayer); else (boundaryLayer as any).addTo(map);
    }
  };

  const handleOpacityChange = (layerId: string, newOpacity: number) => {
    setLayers(prev => prev.map(l => l.id === layerId ? { ...l, opacity: newOpacity } : l));
    const map = mapRef.current as any;
    if (!map) return;
    if (layerId === 'boundary-fill') {
      const b = map._boundaryLayer as L.GeoJSON | undefined;
      if (b) b.setStyle({ fillOpacity: newOpacity });
      return;
    }
    const deviceGroups: Record<string, L.LayerGroup> = map._deviceLayerGroups || {};
    const group = deviceGroups[layerId];
    if (group) group.eachLayer((lyr: any) => { if (lyr && typeof lyr.setStyle === 'function') try { lyr.setStyle({ fillOpacity: newOpacity, opacity: newOpacity }); } catch(e){} });
  };

  return (
    <div style={{ position: "relative", height: "65vh", width: "99%" }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '8px 15px', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: '13px' }}>Devices:</span>
        {DEVICE_LAYERS.map(({ type, color, label }: any) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
            <div style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: color, border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.35)', flexShrink: 0 }} />
            <span>{label}</span>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
          <div style={{ width: '14px', height: '14px', backgroundColor: 'rgba(144,238,144,0.6)', border: '1.5px solid #006400', borderRadius: '2px' }} />
          <span>Site Boundary</span>
        </div>
      </div>

      <div ref={mapContainer} style={{ position: 'absolute', top: 40, bottom: 0, width: '100%' }} />

      <LayerControl
        layers={layers}
        activeLayers={activeLayers}
        toggleLayerVisibility={toggleLayerVisibility}
        handleOpacityChange={handleOpacityChange}
        mapStyle={mapStyle}
        setMapStyle={setMapStyle}
      />
    </div>
  );
};

export default DMapAnalysis;
